/* eslint-disable camelcase */
import { FIELD_NAMES } from 'consts';
import BasicFormWizardContext from 'hooks/contexts/BasicFormWizardContext';
import React, { useContext, useMemo } from 'react';
// @ts-expect-error - React Select is going to be removed, its a legacy component
import { ValueType } from 'react-select';
import { externalSuperFetch } from 'utils/superFetch';
// @ts-expect-error - React Select is going to be removed, its a legacy component
import AsyncSelect from 'react-select/async';
import {
  FormFieldProps,
  GoogleAutoCompleteResult,
  GooglePlace,
  TriadGooglePlaceApiResponse,
  ErrorPriority,
} from 'types';
import { LogError } from 'utils/logging/LogError';
import promiseDebounce from 'utils/promiseDebounce';
import { getGooglePlaceAddressParts } from './getGooglePlaceAddressParts';
import { GoogleMap } from './GoogleMap';

const { CITY, STATE, STREET_ADDRESS, ZIP } = FIELD_NAMES;

export const debouncedGetGooglePlaces: (
  searchQuery: string
) => Promise<TriadGooglePlaceApiResponse<GoogleAutoCompleteResult[]>> =
  promiseDebounce(
    (searchQuery: string) =>
      externalSuperFetch('/api/google/placeAutocomplete', {
        timeout: 3000,
        queryParams: { searchQuery },
      }),
    800
  );

/**
 * @summary Google Address Lookup component
 */
export function GoogleAddressLookup(props: FormFieldProps): React.JSX.Element {
  const { name, value, onChange, onFocus, onBlur } = props;
  const [options, setOptions] = React.useState<GooglePlace[]>([]);
  const { actions } = useContext(BasicFormWizardContext);

  const currentValue = useMemo(() => {
    if (
      !value ||
      typeof value === 'string' ||
      Array.isArray(value) ||
      typeof value === 'boolean'
    ) {
      return null;
    }

    return { ...value, value: value.value as GooglePlace };
  }, [value]);

  const _onChange = async (
    selection: ValueType<GooglePlace, false>
  ): Promise<void> => {
    if (selection && selection.placeId) {
      actions.setStatus({ isProcessing: true });

      const [apiError, response] = await externalSuperFetch<
        TriadGooglePlaceApiResponse<GooglePlace | null>
      >('/api/google/placeDetails', {
        timeout: 3000,
        queryParams: { placeId: selection.placeId },
      });

      if (apiError) {
        LogError('GoogleAddressLookup: Failed to fetch Google Place details', {
          description: apiError.message,
          priority: ErrorPriority.ONE,
        });
        return;
      }

      const { data: googlePlace, success, error } = response;

      if (!success || !googlePlace) {
        LogError('GoogleAddressLookup: Failed to fetch Google Place details', {
          priority: ErrorPriority.ONE,
          description: error || 'Failed to fetch Google Place details',
        });
        return;
      }

      const updateFieldValue = {
        ...googlePlace,
        placeId: selection.placeId,
      };

      const { street, city, zip, state } =
        getGooglePlaceAddressParts(updateFieldValue);

      onChange(zip, { name: ZIP, shouldSkipFormChangeFlow: true });
      onChange(city, { name: CITY, shouldSkipFormChangeFlow: true });
      onChange(state, { name: STATE, shouldSkipFormChangeFlow: true });
      onChange(street, {
        name: STREET_ADDRESS,
        shouldSkipFormChangeFlow: true,
      });

      if (currentValue) {
        onChange({ ...currentValue, value: updateFieldValue }, { name });
      } else {
        onChange({ value: updateFieldValue }, { name });
      }

      actions.setStatus({ isProcessing: false });
    } else {
      LogError('GoogleAddressLookup: Invalid selection', {
        priority: ErrorPriority.ONE,
        description: `selection: ${selection}`,
      });
    }
  };

  function handleLoadOptions(inputValue: string): Promise<GooglePlace[]> {
    return debouncedGetGooglePlaces(inputValue)
      .then((response) => {
        const googlePlaces = response.data.map((result) => ({
          formattedAddress: result.placePrediction.text.text,
          placeId: result.placePrediction.placeId,
          addressComponents: [],
          location: { latitude: 0, longitude: 0 },
        }));

        setOptions(googlePlaces);
        return googlePlaces;
      })
      .catch((error) => {
        LogError('Failed to fetch Google Places', {
          priority: ErrorPriority.ONE,
          description: (error as Error).message,
        });
        return [];
      });
  }

  return (
    <>
      <GoogleMap mapUrl={currentValue?.value.staticMapUrl} />
      <AsyncSelect
        isSearchable
        cacheOptions
        menuShouldScrollIntoView
        defaultOptions={options}
        tabIndex="0"
        loadOptions={handleLoadOptions}
        name={name}
        classNamePrefix="select"
        onChange={_onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        minMenuHeight={225}
        inputId="google-address-lookup"
        value={currentValue?.value}
        getOptionLabel={(option: GooglePlace) => option.formattedAddress}
        getOptionValue={(option: GooglePlace) => option.formattedAddress}
        formatOptionLabel={(option: GooglePlace) => option.formattedAddress}
      />
    </>
  );
}
